import React, { useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { showGlobalToast, Spinner, ErrorBanner, EmptyState } from '../../components/ui';
import { fmt, errMsg } from '../../utils/format';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const typeLabel = (t) => t.replace(/_/g,' ');
const isCrit = (t) => t.includes('critical') || t.includes('stockout') || t.includes('out_of_stock') || t.includes('zero');

export default function Alerts() {
  const { data: log, loading, error, refetch } = useFetch('/api/alerts?limit=100');
  const { data: active, refetch: refetchActive } = useFetch('/api/alerts/active');
  const { isAdmin } = useAuth();
  const [running, setRunning] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data: r } = await api.post('/api/alerts/evaluate');
      showGlobalToast(`${r.triggered} alert(s) evaluated`);
      refetch(); refetchActive();
    } catch (err) { alert(errMsg(err)); }
    finally { setRunning(false); }
  };

  const sendTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data: r } = await api.post('/api/alerts/test');
      setTestResult({ success: true, message: r.message });
      showGlobalToast('Test email sent successfully');
    } catch (err) {
      setTestResult({ success: false, message: errMsg(err) });
    } finally { setTesting(false); }
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} onRetry={refetch} />;

  const activeAlerts = active || [];

  return (
    <div>
      <div className="page-header">
        <h1>Alerts</h1>
        <div className="page-actions">
          {isAdmin && <>
            <button className="btn btn-sm" onClick={sendTest} disabled={testing}>
              <i className="ti ti-mail" /> {testing ? 'Sending…' : 'Send test email'}
            </button>
            <button className="btn btn-sm" onClick={runNow} disabled={running}>
              <i className="ti ti-bell" /> {running ? 'Running…' : 'Evaluate now'}
            </button>
          </>}
        </div>
      </div>

      {testResult && (
        <div className={`alert ${testResult.success ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom:12 }}>
          <i className={`ti ${testResult.success ? 'ti-check' : 'ti-alert-circle'}`} />
          {testResult.message}
        </div>
      )}

      {/* Active alerts with PO status */}
      {activeAlerts.length > 0 && (
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', fontWeight:600, fontSize:13 }}>
            <i className="ti ti-alert-triangle" style={{ marginRight:6, color:'var(--color-warning)' }} />
            Active alerts ({activeAlerts.length})
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Message</th>
                  <th>First seen</th>
                  <th>PO status</th>
                  <th>Expected arrival</th>
                </tr>
              </thead>
              <tbody>
                {activeAlerts.map(a => (
                  <tr key={a.id}>
                    <td>
                      <span className={`badge ${isCrit(a.alert_type) ? 'badge-crit' : 'badge-amber'}`} style={{ fontSize:10 }}>
                        {typeLabel(a.alert_type)}
                      </span>
                    </td>
                    <td style={{ fontSize:12 }}>{a.message}</td>
                    <td style={{ fontSize:12 }}>{fmt.date(a.first_seen_at)}</td>
                    <td>
                      {a.po_issued
                        ? <span className="badge badge-info" style={{ fontSize:10 }}>
                            <i className="ti ti-check" style={{ marginRight:3 }} />
                            PO issued{a.po_number ? ` — ${a.po_number}` : ''}{a.po_vendor ? ` (${a.po_vendor})` : ''}
                          </span>
                        : <span className="badge" style={{ fontSize:10, background:'var(--bg-tertiary)', color:'var(--text-secondary)' }}>No PO</span>
                      }
                    </td>
                    <td style={{ fontSize:12 }}>
                      {a.expected_arrival_date
                        ? <span style={{ color: new Date(a.expected_arrival_date) < new Date() ? 'var(--color-danger)' : 'var(--color-success)' }}>
                            {fmt.date(a.expected_arrival_date)}
                          </span>
                        : <span style={{ color:'var(--text-tertiary)' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alert log */}
      <div className="card">
        <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', fontWeight:600, fontSize:13 }}>
          Alert log
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Type</th><th>Message</th><th>Sent</th></tr></thead>
            <tbody>
              {(log||[]).map(a => (
                <tr key={a.id}>
                  <td><span className={`badge ${isCrit(a.alert_type) ? 'badge-crit' : 'badge-amber'}`} style={{ fontSize:10 }}>{typeLabel(a.alert_type)}</span></td>
                  <td style={{ fontSize:12 }}>{a.message}</td>
                  <td style={{ fontSize:12 }}>{fmt.dateTime(a.sent_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!(log||[]).length && <EmptyState icon="ti-bell" message="No alerts logged yet" />}
        </div>
      </div>
    </div>
  );
}
